"use client";

import React from "react";

/**
 * CSS custom properties for a branded section background.
 * Resolved server-side from a Style document and passed as a serializable prop.
 */
export interface BrandedStyle {
    "--background-gradient-from"?: string;
    "--background-gradient-to"?: string;
    "--background-gradient-direction"?: string;
    "--background-image-url"?: string;
    "--background-text-color"?: string;
    "--background-text-color-muted"?: string;
    /** Resolved rem string (e.g. "-2.5rem"). Undefined = no override. */
    marginTop?: string;
    marginRight?: string;
    marginBottom?: string;
    marginLeft?: string;
    bleed?: boolean;
    /**
     * When true, only `color` is applied to the wrapper — `--foreground` and
     * `--muted-foreground` are left at tenant defaults so child cards using
     * text-foreground / text-muted-foreground stay readable.
     */
    preserveChildTextTokens?: boolean;
}

interface SectionStripProps {
    /** Resolved style from CMS. If undefined, children render unwrapped. */
    brandedStyle?: BrandedStyle;
    /** Additional className for the outer wrapper */
    className?: string;
    children: React.ReactNode;
}

/**
 * Branded background layer — rendered as an absolute-positioned div
 * behind the section content.
 */
function BrandedBackground({ brandedStyle }: { brandedStyle: BrandedStyle }) {
    const gradientFrom = brandedStyle["--background-gradient-from"] || "transparent";
    const gradientTo = brandedStyle["--background-gradient-to"] || gradientFrom;
    const direction = brandedStyle["--background-gradient-direction"] || "to bottom";
    const imageUrl = brandedStyle["--background-image-url"];

    const parts: string[] = [];
    if (imageUrl) parts.push(imageUrl);
    parts.push(`linear-gradient(${direction}, ${gradientFrom}, ${gradientTo})`);

    return (
        <div
            aria-hidden
            className="absolute inset-0 z-0 pointer-events-none"
            style={{
                backgroundImage: parts.join(", "),
                backgroundSize: "cover",
                backgroundPosition: "center",
                backgroundRepeat: "no-repeat",
            }}
        />
    );
}

/**
 * SectionStrip — wraps content in a visually distinct horizontal band.
 *
 * All styling comes from the CMS-assigned Style document via `brandedStyle`.
 * If no style is assigned (brandedStyle is undefined), children render unwrapped.
 *
 * Sets --foreground and --muted-foreground CSS variables from the style's text color
 * so child text adapts automatically. Does NOT override --card, --card-foreground,
 * --border, etc. — cards inside branded sections keep their tenant default colors.
 *
 * Bleed (full-width background) is controlled by the style's `bleed` flag.
 */
export function SectionStrip({ brandedStyle, className = "", children }: SectionStripProps) {
    if (!brandedStyle) {
        return <>{children}</>;
    }

    const hasBackground = brandedStyle["--background-gradient-from"] || brandedStyle["--background-image-url"];

    const containerStyle: React.CSSProperties & Record<string, string> = {};
    if (brandedStyle["--background-text-color"]) {
        containerStyle.color = brandedStyle["--background-text-color"];
        if (!brandedStyle.preserveChildTextTokens) {
            containerStyle["--foreground"] = brandedStyle["--background-text-color"];
        }
    }
    if (brandedStyle["--background-text-color-muted"] && !brandedStyle.preserveChildTextTokens) {
        containerStyle["--muted-foreground"] = brandedStyle["--background-text-color-muted"];
    }
    // Bleed strips manage horizontal margins themselves — see below.
    if (brandedStyle.marginTop) containerStyle.marginTop = brandedStyle.marginTop;
    if (brandedStyle.marginBottom) containerStyle.marginBottom = brandedStyle.marginBottom;
    if (brandedStyle.marginRight) containerStyle.marginRight = brandedStyle.marginRight;
    if (brandedStyle.marginLeft) containerStyle.marginLeft = brandedStyle.marginLeft;

    if (brandedStyle.bleed) {
        return (
            <div
                className={`relative py-8 overflow-hidden ${className}`}
                style={{
                    ...containerStyle,
                    marginLeft: "calc(-50vw + 50%)",
                    marginRight: "calc(-50vw + 50%)",
                }}
            >
                {hasBackground && <BrandedBackground brandedStyle={brandedStyle} />}
                <div className="relative z-10 max-w-350 mx-auto px-4">{children}</div>
            </div>
        );
    }

    return (
        <div className={`relative px-4 py-6 overflow-hidden ${className}`} style={containerStyle}>
            {hasBackground && <BrandedBackground brandedStyle={brandedStyle} />}
            <div className="relative z-10">{children}</div>
        </div>
    );
}
